import { getOfflineDb, type QueuedVoiceNote, type QueuedVoiceNoteStatus } from './db';
import type { VoiceNoteContentType, VoiceNotePurpose } from '../api/types';
import {
  createVoiceNote as createVoiceNoteRoute,
  requestVoiceNoteUpload as requestVoiceNoteUploadRoute,
} from '../api/capture';
import { uploadAssetBytes } from '../api/client';

/**
 * The offline queue for voice clips (INTERFACES.md 15.3, W1). A clip
 * recorded with no signal wait here with the rest of the capture, keyed
 * either to a `captureId` already known or to the `queue` row it travels
 * with (`db.ts` `QueuedVoiceNote`), and uploads on reconnect the same way
 * `queue.ts` uploads a capture: presign, PUT the bytes, then register.
 *
 * Mirrors `queue.ts`'s own retry budget, because a note recorded offline
 * for hours must not burn the battery on an unbounded retry loop either.
 */
const MAX_AUTO_RETRY_ATTEMPTS = 8;

export interface EnqueueVoiceNoteInput {
  /** Exactly one of these two is set; see `QueuedVoiceNote`'s own note. */
  captureId?: string;
  queuedCaptureLocalId?: string;
  contentType: VoiceNoteContentType;
  durationMs: number;
  purpose: VoiceNotePurpose;
  languageCode?: string;
  bytes: Blob;
}

/** Writes a voice clip to IndexedDB as 'pending' and returns immediately,
 *  with no network required. */
export async function enqueueVoiceNote(input: EnqueueVoiceNoteInput): Promise<QueuedVoiceNote> {
  const db = await getOfflineDb();
  const now = new Date().toISOString();
  const { bytes, ...rest } = input;
  const record: QueuedVoiceNote = {
    localId: crypto.randomUUID(),
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    byteLength: bytes.size,
    ...rest,
    bytes: await bytes.arrayBuffer(),
  };
  await db.put('voiceNotes', record);
  return record;
}

export interface EnqueueTranscriptNoteInput {
  /** Exactly one of these two is set; see `QueuedVoiceNote`'s own note. */
  captureId?: string;
  queuedCaptureLocalId?: string;
  transcript: string;
  durationMs: number;
  purpose: VoiceNotePurpose;
  languageCode?: string;
}

/**
 * Queues the text of a live transcription (CAPTURE_JOBS.md section 3)
 * against a capture, with no network required. The viewfinder records
 * the note before the capture has a server id, so it travels with the
 * capture's `queue` row and registers once that row has uploaded.
 *
 * The `noteId` is minted here, once, so a retry after a lost response
 * registers the same note rather than a second copy of it.
 */
export async function enqueueTranscriptNote(input: EnqueueTranscriptNoteInput): Promise<QueuedVoiceNote> {
  const db = await getOfflineDb();
  const now = new Date().toISOString();
  const record: QueuedVoiceNote = {
    localId: crypto.randomUUID(),
    noteId: crypto.randomUUID(),
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    byteLength: 0,
    ...input,
  };
  await db.put('voiceNotes', record);
  return record;
}

export async function listVoiceNoteQueue(): Promise<QueuedVoiceNote[]> {
  const db = await getOfflineDb();
  return db.getAll('voiceNotes');
}

export async function listVoiceNotesByStatus(status: QueuedVoiceNoteStatus): Promise<QueuedVoiceNote[]> {
  const db = await getOfflineDb();
  return db.getAllFromIndex('voiceNotes', 'by-status', status);
}

interface VoiceNoteUploader {
  requestVoiceNoteUpload: typeof requestVoiceNoteUploadRoute;
  createVoiceNote: typeof createVoiceNoteRoute;
  uploadAssetBytes: typeof uploadAssetBytes;
}

const defaultUploader: VoiceNoteUploader = {
  requestVoiceNoteUpload: requestVoiceNoteUploadRoute,
  createVoiceNote: createVoiceNoteRoute,
  uploadAssetBytes,
};

export interface ProcessVoiceNoteQueueResult {
  uploaded: string[];
  failed: string[];
  stillPending: string[];
  /** Recorded with `queuedCaptureLocalId` and that capture has not
   *  reached the server yet, so there is no `captureId` to upload
   *  against. Retried on the next drain, once `processQueue` (queue.ts)
   *  has delivered the capture it travels with. */
  waitingOnCapture: string[];
}

/**
 * Resolves a note's `queuedCaptureLocalId` to the `captureId` its `queue`
 * row now carries, or `undefined` while that capture is still pending.
 * Reads the `queue` store directly rather than importing `queue.ts`, so
 * the two queues stay one-way dependent: `sync.ts` drains the capture
 * queue before the voice-note queue, and this is what lets a note wait
 * for that without either module importing the other's processing loop.
 */
async function resolveCaptureId(
  db: Awaited<ReturnType<typeof getOfflineDb>>,
  note: QueuedVoiceNote,
): Promise<string | undefined> {
  if (note.captureId) return note.captureId;
  if (!note.queuedCaptureLocalId) return undefined;
  const queued = await db.get('queue', note.queuedCaptureLocalId);
  return queued?.captureId;
}

/**
 * Drains every 'pending' voice note against the server. Called on
 * reconnect (sync.ts) after the capture queue, or manually. `uploader` is
 * injectable so tests can prove the presign/PUT/register sequence with no
 * network, the same pattern `queue.ts` `processQueue` uses.
 */
export async function processVoiceNoteQueue(
  uploader: Partial<VoiceNoteUploader> = {},
): Promise<ProcessVoiceNoteQueueResult> {
  const merged: VoiceNoteUploader = { ...defaultUploader, ...uploader };
  const db = await getOfflineDb();
  const pending = await listVoiceNotesByStatus('pending');
  const result: ProcessVoiceNoteQueueResult = {
    uploaded: [],
    failed: [],
    stillPending: [],
    waitingOnCapture: [],
  };

  for (const note of pending) {
    const captureId = await resolveCaptureId(db, note);
    if (!captureId) {
      result.waitingOnCapture.push(note.localId);
      continue;
    }

    note.status = 'uploading';
    note.updatedAt = new Date().toISOString();
    await db.put('voiceNotes', note);

    try {
      if (note.transcript !== undefined) {
        // Text only: no audio to presign or upload.
        await merged.createVoiceNote(captureId, {
          noteId: note.noteId ?? note.localId,
          transcript: note.transcript,
          durationMs: note.durationMs,
          purpose: note.purpose,
          ...(note.languageCode ? { languageCode: note.languageCode } : {}),
        });
        await db.delete('voiceNotes', note.localId);
        result.uploaded.push(note.localId);
        continue;
      }
      if (!note.bytes || !note.contentType) {
        throw new Error('This voice note has neither audio nor text to send.');
      }
      const uploadUrlResponse = await merged.requestVoiceNoteUpload(captureId, {
        contentType: note.contentType,
        byteLength: note.byteLength,
        durationMs: note.durationMs,
        purpose: note.purpose,
      });
      await merged.uploadAssetBytes(
        uploadUrlResponse.uploadUrl,
        new Blob([note.bytes], { type: note.contentType }),
        note.contentType,
      );
      await merged.createVoiceNote(captureId, {
        noteId: uploadUrlResponse.noteId,
        audioKey: uploadUrlResponse.audioKey,
        durationMs: note.durationMs,
        purpose: note.purpose,
        ...(note.languageCode ? { languageCode: note.languageCode } : {}),
      });
      await db.delete('voiceNotes', note.localId);
      result.uploaded.push(note.localId);
    } catch (error) {
      note.attempts += 1;
      note.lastError = error instanceof Error ? error.message : 'Unknown upload error';
      note.updatedAt = new Date().toISOString();
      if (note.attempts >= MAX_AUTO_RETRY_ATTEMPTS) {
        note.status = 'failed';
        result.failed.push(note.localId);
      } else {
        note.status = 'pending';
        result.stillPending.push(note.localId);
      }
      await db.put('voiceNotes', note);
    }
  }

  return result;
}
